/**
 * @template T
 * Establece un valor anidado por ruta (inmutable). Admite notación de puntos e índices de corchetes (p. ej., "a.b[0].c").
 *
 * - Si faltan objetos/matrices intermedios, se crean.
 * - Las matrices se copian y los objetos se copian superficialmente en la ruta modificada.
 *
 * @param {T} obj - Objeto de origen (no se mutará).
 * @param {string} path - Ruta como "a.b.c" o "a.b[0].c" o "a.0.c".
 * @param {*} value - Valor a establecer en la ruta proporcionada.
 * @returns {T} Un nuevo objeto con el valor establecido en la ruta.
 *
 * @example
 * const o1 = { evaluacion: { nro: 1, nombre: 'a' } };
 * const r1 = setByPath(o1, 'evaluacion.nro', 2);
 * // r1 => { evaluacion: { nro: 2, nombre: 'a' } }
 *
 * @example
 * const o2 = { items: [{ name: 'x' }] };
 * const r2 = setByPath(o2, 'items[0].name', 'y');
 * // r2 => { items: [{ name: 'y' }] }
 *
 * @example
 * const r3 = setByPath({}, 'a.b[2].c', 10);
 * // r3 => { a: { b: [ , , { c: 10 } ] } }
 */
export default function setByPath(obj, path, value) {
    // Normalize "a[0].b" -> "a.0.b" and split
    var segments = path
        .replace(/\[(\w+)\]/g, '.$1')
        .replace(/^\./, '')
        .split('.');

    function assign(curr, keys, val) {
        var k = keys[0];
        var rest = keys.slice(1);
        var isIndex = /^\d+$/.test(k);

        var base = (curr === undefined || curr === null)
            ? (isIndex ? [] : {})
            : curr;

        if (rest.length === 0) {
            if (Array.isArray(base)) {
                var copyA = base.slice();
                copyA[Number(k)] = val;
                return copyA;
            }
            return Object.assign({}, base, { [k]: val });
        }

        var nextKey = isIndex ? Number(k) : k;
        var nextNode = assign(base[nextKey], rest, val);

        if (Array.isArray(base)) {
            var copyArr = base.slice();
            copyArr[nextKey] = nextNode;
            return copyArr;
        }

        return Object.assign({}, base, { [k]: nextNode });
    }

    return assign(obj, segments, value);
}
